import { useEffect } from 'react';
import { useMutation } from '@apollo/client';
import useCurrentUser from 'hooks/useCurrentUser';
import { SAVE_SYNC_UW_CALCULATOR_MAX_INVESTMENT_DATA } from './mutations';
import InvestmentCalculator from './components/InvestmentCalculator/InvestmentCalculator';
import StatusCodeErrorBlock from 'components/StatusCodeErrorBlock/StatusCodeErrorBlock';
import { currency, currencyWithCents, percentage } from 'utils/numbers';
import AlertError from 'components/AlertError/AlertError';
import PaydownError from './components/PaydownError/PaydownError';
import { INPUT_FIELDS, INPUT_GROUPS } from './utils/calculatorConstants';
import { BROADCAST_CHANNELS, useBroadcastChannel } from 'hooks/useBroadcastChannel';
import { getApplicationReviewPageUrls, getTrackDetailPageUrls } from 'apps/track-details/utils/trackDetailsLinks';
import {
  riskBand6ErrorFromCriteria,
  rentalDebtServiceCoverageRatioErrorFromCriteria,
  rentalLienPositionErrorFromCriteria,
  rentalCompositeFicoErrorFromCriteria,
} from './utils/calculatorErrors';
import useTrack from 'hooks/useTrackQuery';
import LoadingContainer from 'components/LoadingContainer/LoadingContainer';
import { CONFIGURATIONS_RESIDENCE_TYPE } from 'data/constants/configurations';
import { APP_REVIEW_ANCHORS } from '../track-details/ApplicationReview/data/constants/appReviewAnchors';
import './InvestmentCalculator.scss';
import Callout from 'components/Callout/Callout';

const NA = 'N/A';

const UWCalculatorController = () => {
  const { isInUnderwriterGroup, user } = useCurrentUser();
  const { track, refetchTrack } = useTrack();
  const trackId = track?.identifier;

  const [
    saveSyncMutation,
    {
      data: saveSyncData,
      loading: uwCalcMaxInvestmentLoading,
      called: saveSyncMutationCalled,
      error: uwCalcMaxInvestmentError,
    },
  ] = useMutation(SAVE_SYNC_UW_CALCULATOR_MAX_INVESTMENT_DATA, {
    onCompleted: result => {
      updateTrackData(result.uwSaveSyncMaxInvestment?.track);
    },
  });

  const latestUwCalculatorOutput = saveSyncData?.uwSaveSyncMaxInvestment?.track?.latestUwCalculatorOutput;
  const uwCalcMaxInvestmentData = {
    ...(latestUwCalculatorOutput || {}),
    // Fix for the rounded value issue in getOutputFields (InvestmentCalculator)
    homeownerEndingEquity: (latestUwCalculatorOutput?.homeownerEndingEquity ?? 0) / 100,
  };
  const uwCalcMaxInvestmentErrors = saveSyncData?.uwSaveSyncMaxInvestment?.errors;

  const { postMessage } = useBroadcastChannel({
    channelName: BROADCAST_CHANNELS.UNDERWRITING_CALCULATOR_SAVED,
  });

  const updateTrackData = track => {
    const message = {
      trackId,
      track,
    };
    postMessage(message);
  };

  useEffect(() => {
    if (!saveSyncMutationCalled && trackId) {
      saveSyncMutation({
        variables: { trackId },
      });
    }
  }, [saveSyncMutation, trackId, saveSyncMutationCalled]);

  if (!isInUnderwriterGroup) {
    return <StatusCodeErrorBlock errorCode={403} />;
  }

  const { liensUrl, applicationReviewUrl } = getApplicationReviewPageUrls(trackId, user?.rolloutFlags);
  const { homeValuationsUrl } = getTrackDetailPageUrls(trackId, user?.rolloutFlags);

  const rb6Error = riskBand6ErrorFromCriteria(uwCalcMaxInvestmentData?.uwFailedEligibilityCriteria);
  const compositeFicoError = rentalCompositeFicoErrorFromCriteria(uwCalcMaxInvestmentData?.uwFailedEligibilityCriteria);
  const lienPositionError = rentalLienPositionErrorFromCriteria(uwCalcMaxInvestmentData?.uwFailedEligibilityCriteria);
  const debtServiceCoverageRatioError = rentalDebtServiceCoverageRatioErrorFromCriteria(
    uwCalcMaxInvestmentData?.uwFailedEligibilityCriteria,
  );

  const thirdLienAppliedCallout = uwCalcMaxInvestmentData?.thirdLienCapApplied && (
    <Callout type="warning">
      Max Investment amount set to <strong>{currency(track.thirdLienCap, true)}</strong> per Hometap's anticipated lien
      position.
    </Callout>
  );

  const valueOrNA = (value, func) => {
    return value ? func(value) : NA;
  };

  const { MORTGAGE_LIENS } = APP_REVIEW_ANCHORS;
  const payDownLink = `${applicationReviewUrl}${MORTGAGE_LIENS}`;

  const rb6Criteria = track?.rb6Criteria ?? '-';

  const highestSingleYearDelinquentTaxes = track?.highestSingleYearDelinquentTaxes
    ? currencyWithCents(track.highestSingleYearDelinquentTaxes)
    : '-';

  const baseGroups = [
    {
      group: INPUT_GROUPS.PROPERTY,
      rows: [
        {
          label: INPUT_FIELDS.HOME_VALUE,
          value: valueOrNA(track.beginningHomeValuation?.value, currency),
          link: homeValuationsUrl,
          additional: !track.beginningHomeValuation && <AlertError error="Please set a beginning home value" />,
          tooltipContent: (
            <>
              This value is being pulled from <b>Beginning Home Valuation.</b>
            </>
          ),
        },
        {
          label: INPUT_FIELDS.DEBT,
          value: currencyWithCents(track.totalDebtOnHome),
          link: liensUrl,
          tooltipContent: (
            <>
              This value is being pulled from <b>Total Lien Balance.</b>
            </>
          ),
        },
        {
          label: INPUT_FIELDS.STARTING_EQUITY,
          value: valueOrNA(track.startingEquityPercent, percentage),
        },
      ],
    },
    {
      group: INPUT_GROUPS.RISK_BAND,
      rows: [
        {
          label: INPUT_FIELDS.IWRB,
          value: track.incomeWeightedRiskBand.value || 'None',
        },
        {
          label: INPUT_FIELDS.RB6,
          value: rb6Criteria,
          additional: rb6Error && <AlertError error={rb6Error} />,
        },
      ],
    },
    {
      group: INPUT_GROUPS.PAYOFFS,
      rows: [
        {
          label: INPUT_FIELDS.FEE_PAYDOWN,
          value: valueOrNA(track.feePaydown, currencyWithCents),
          link: payDownLink,
          tooltipContent: (
            <>
              This value is being calculated from <b>Total Paydown minus Principal Paydown.</b>
            </>
          ),
        },
        {
          label: INPUT_FIELDS.PRINCIPAL_PAYDOWN,
          value: valueOrNA(track.principalPaydown, currencyWithCents),
          link: payDownLink,
          additional: <PaydownError calculatedData={uwCalcMaxInvestmentData} includeCents={true} />,
          tooltipContent: (
            <>
              This value is being pulled from <b>Principal Paydown.</b>
            </>
          ),
        },
        {
          label: INPUT_FIELDS.TOTAL_PAYDOWN,
          value: valueOrNA(track.totalPaydown, currencyWithCents),
          link: null,
        },
      ],
    },
    {
      group: INPUT_GROUPS.WITHHOLDINGS,
      rows: [
        {
          label: INPUT_FIELDS.DELINQUENT_TAXES,
          value: highestSingleYearDelinquentTaxes,
        },
        {
          label: INPUT_FIELDS.WITHHOLDING_TOTAL,
          value: currencyWithCents(uwCalcMaxInvestmentData?.withholdingTotal),
        },
      ],
    },
  ];

  let compositeFicoAlertError = null;
  if (track.compositeFicoData?.errorMessage) {
    compositeFicoAlertError = <AlertError error={track.compositeFicoData?.errorMessage} />;
  } else if (compositeFicoError) {
    compositeFicoAlertError = <AlertError error={compositeFicoError} />;
  }

  if (track.home?.residenceType === CONFIGURATIONS_RESIDENCE_TYPE.Rental) {
    baseGroups.push({
      group: INPUT_GROUPS.RENTAL_ELIGIBILITY,
      rows: [
        {
          label: INPUT_FIELDS.COMPOSITE_FICO,
          value: track.compositeFicoData?.value ?? 'Missing',
          additional: compositeFicoAlertError,
        },
        {
          label: INPUT_FIELDS.LIEN_POSITION,
          value: valueOrNA(track.htLienPosition, val => val),
          additional: lienPositionError && <AlertError error={lienPositionError} />,
        },
        {
          label: INPUT_FIELDS.DEBT_SERVICE_COVERAGE_RATIO,
          value: track.debtServiceCoverageRatioInfo.dscrValue ?? 'Incomplete',
          additional: debtServiceCoverageRatioError && <AlertError error={debtServiceCoverageRatioError} />,
        },
      ],
    });
  }

  // Save & Sync error handling:
  // - uwCalcMaxInvestmentErrors handled errors from UW Calc mutation response
  // - uwCalcMaxInvestmentError fallback GraphQL exception
  const headerSaveSyncCalcError =
    (!!uwCalcMaxInvestmentErrors?.length && uwCalcMaxInvestmentErrors) ||
    (uwCalcMaxInvestmentError && 'Save & Sync of Maximum Investment Failed');

  return (
    <div>
      <LoadingContainer isLoading={uwCalcMaxInvestmentLoading}>
        <InvestmentCalculator
          track={track}
          loading={uwCalcMaxInvestmentLoading}
          headerError={headerSaveSyncCalcError}
          calculatedData={uwCalcMaxInvestmentData}
          calculationsError={uwCalcMaxInvestmentError}
          onFetchTrack={refetchTrack}
          inputGroups={baseGroups}
          outputHeader={thirdLienAppliedCallout}
        />
      </LoadingContainer>
    </div>
  );
};

export default UWCalculatorController;
